import moment from "moment"
import { DATEFORMAT } from "../../constants"

/** This is the default state before any user-based filter restrictions are loaded */
const initialFilterState = {
  activeField: null,
  advancedFiltersOpen: false,
  errorMessageKey: null,
  filtersPinned: true,
  showFiltersFullScreen: false,
  fields: {
    appraisalLevels: {
      display: {
        text: null,
      },
      isLoading: true,
      options: [],
      scrollTop: 0,
      selectedValue: null,
    },
    dataMode: {
      display: {
        i18nKey: "Filters.Standard.DataMode.Options.Live",
      },
      options: [
        { label: "Filters.Standard.DataMode.Options.Live", value: "LIVE", selected: true },
        { label: "Filters.Standard.DataMode.Options.Historic", value: "STATIC", selected: false },
      ],
      selectedValue: "LIVE",
    },
    endDate: {
      display: {
        text: null,
      },
      maxValue: moment(new Date()), // max date of today
      minValue: null,
      selectedValue: null,
    },
    generations: {
      display: {
        text: null,
      },
      isLoading: true,
      options: [],
      scrollTop: 0,
      selectedValue: null,
    },
    genders: {
      display: {
        text: null,
      },
      isLoading: true,
      options: [],
      scrollTop: 0,
      selectedValue: null,
    },
    jobTitles: {
      display: {
        text: null,
      },
      isLoading: true,
      options: [],
      scrollTop: 0,
      selectedValue: null,
    },
    locations: {
      display: {
        text: null,
      },
      isLoading: true,
      options: [],
      selectedValue: null,
    },
    adminLocations: {
      display: {
        text: null,
      },
      isLoading: true,
      options: [],
      selectedValue: null,
    },
    review: {
      display: {
        text: "None",
      },
      isLoading: true,
      options: [],
      selectedValue: "-1",
    },
    startDate: {
      display: {
        text: null,
      },
      maxValue: moment(new Date()), // max date of today
      minValue: null,
      selectedValue: null,
    },
    managers: {
      display: {
        text: "None",
      },
      isLoading: true,
      options: [],
      selectedValue: -1,
    },
    viewMode: {
      display: {
        i18nKey: null,
      },
      isLoading: true,
      options: [],
      selectedValue: null,
    },
  },
}

/** Manipulate the filter state depending on the action type/params */
export default function filterReducer(state = initialFilterState, action) {
  /** Remember that state is immutable - don't change/mutate
   * the incoming state, create a new state object and return that
   */
  switch (action.type) {
    case "LOADFILTERS_SUCCESS":
      return {
        ...state,
        errorMessageKey: null,
        fields: {
          ...state.fields,

          appraisalLevels: {
            ...state.fields.appraisalLevels,
            isLoading: false,
            options: action.payload.data.appraisalLevels,
          },

          generations: {
            ...state.fields.generations,
            isLoading: false,
            options: action.payload.data.generations,
          },

          genders: {
            ...state.fields.genders,
            isLoading: false,
            options: action.payload.data.genders,
          },

          jobTitles: {
            ...state.fields.jobTitles,
            isLoading: false,
            options: action.payload.data.jobTitles,
          },

          locations: {
            ...state.fields.locations,
            isLoading: false,
            options: action.payload.data.locations,
          },

          adminLocations: {
            ...state.fields.adminLocations,
            isLoading: false,
            options: action.payload.data.adminLocations,
          },

          managers: {
            ...state.fields.managers,
            isLoading: false,
            options: action.payload.data.managers,
          },

          review: {
            ...state.fields.review,
            isLoading: false,
            options: action.payload.data.reviews,
          },
        },
      }

    case "LOADFILTERS_FAIL":
      return {
        ...state,
        errorMessageKey: "Filters.Errors.LoadingOptions",
        fields: {
          ...state.fields,

          appraisalLevels: {
            ...state.fields.appraisalLevels,
            isLoading: false,
          },

          generations: {
            ...state.fields.generations,
            isLoading: false,
          },

          genders: {
            ...state.fields.genders,
            isLoading: false,
          },

          jobTitles: {
            ...state.fields.jobTitles,
            isLoading: false,
          },

          locations: {
            ...state.fields.locations,
            isLoading: false,
          },

          adminLocations: {
            ...state.fields.adminLocations,
            isLoading: false,
          },

          managers: {
            ...state.fields.managers,
            isLoading: false,
          },

          review: {
            ...state.fields.review,
            isLoading: false,
          },
        },
      }

    case "CHANGE_VALUE_APPRAISAL_LEVELS":
      // Handle the appraisal levels filter checkbox change event
      // Set `selected` to be whether or not each option's value is in the array of selected values,
      // do not close this active field, set the overall `selectedValue` for this field,
      // and set the display value
      return {
        ...state,
        // activeField: null,
        fields: {
          ...state.fields,
          appraisalLevels: {
            ...state.fields.appraisalLevels,
            display: {
              text: action.displayText || null,
            },
            options: state.fields.appraisalLevels.options.map(option => ({
              ...option,
              selected: action.selectedValues && action.selectedValues.indexOf(option.value) >= 0,
            })),
            scrollTop: action.scrollTop,
            selectedValue: action.selectedValues ? action.selectedValues.join(",") : null,
          },
        },
      }

    // eslint-disable-next-line no-case-declarations
    case "CHANGE_VALUE_DATA_MODE_SUCCESS":
      // Handle the data mode ("report type") filter radio change event
      // Set `selected` to be whether or not the selected value matches the option value,
      // close this active field, set the overall `selectedValue` for this field, and set the
      // display value to be the label of the selected option

      // Also, as INDIRECT (view mode) is incompatible with STATIC (as hierarchies),
      // if STATIC is selected we need to disable INDIRECT and change the value for view mode
      // if INDIRECT is also selected.
      const newMode = action.payload.data.dataMode
      const changeViewModeValue =
        newMode === "STATIC" && state.fields.viewMode.selectedValue === "INDIRECT"
      const isViewModeOptionSelected = (forceValueChange, option, allOptions) => {
        if (!forceValueChange) {
          return option.selected
        }
        let newDefaultValue = "DIRECT"
        if (allOptions.find(opt => opt.value === "ALL")) {
          newDefaultValue = "ALL"
        }
        // Select ALL (if admin, otherwise DIRECT) when the report type is changed to STATIC
        return option.value === newDefaultValue
      }
      const viewModeOptions = state.fields.viewMode.options.map(option => ({
        ...option,
        selected: isViewModeOptionSelected(
          changeViewModeValue,
          option,
          state.fields.viewMode.options
        ),
        disabled: newMode === "STATIC" && option.value === "INDIRECT",
      }))
      const viewModeSelectedOption = viewModeOptions.find(opt => opt.selected === true)
      return {
        ...state,
        activeField: null,
        fields: {
          ...state.fields,
          dataMode: {
            ...state.fields.dataMode,
            display: {
              i18nKey: state.fields.dataMode.options.find(opt => opt.value === newMode).label,
            },
            options: state.fields.dataMode.options.map(option => ({
              ...option,
              selected: option.value === newMode,
            })),
            selectedValue: newMode,
          },
          locations: {
            ...state.fields.locations,
            display: {
              text: null,
            },
            isLoading: false,
            options: action.payload.data.locations,
            selectedValue: null,
          },
          viewMode: {
            ...state.fields.viewMode,
            display: {
              ...state.fields.viewMode.display,
              i18nKey: viewModeSelectedOption.labelShort,
            },
            options: viewModeOptions,
            selectedValue: viewModeSelectedOption.value,
          },
        },
      }

    case "CHANGE_VALUE_END_DATE": {
      // Set the selected date value, and the display date
      // Also sets the maximum value for the start date field
      // to avoid invalid date ranges being selected
      return {
        ...state,
        activeField: null,
        fields: {
          ...state.fields,
          endDate: {
            ...state.fields.endDate,
            display: {
              ...state.fields.endDate.display,
              text: action.newValue ? action.newValue.format(DATEFORMAT) : null,
            },
            selectedValue: action.newValue,
          },
          startDate: {
            ...state.fields.startDate,
            maxValue: action.newValue,
          },
        },
      }
    }

    case "CHANGE_VALUE_GENERATIONS":
      // Handle the generations filter checkbox change event
      // Set `selected` to be whether or not each option's value is in the array of selected values,
      // do not close this active field, set the overall `selectedValue` for this field,
      // and set the display value
      return {
        ...state,
        // activeField: null,
        fields: {
          ...state.fields,
          generations: {
            ...state.fields.generations,
            display: {
              text: action.displayText || null,
            },
            options: state.fields.generations.options.map(option => ({
              ...option,
              selected: action.selectedValues && action.selectedValues.indexOf(option.value) >= 0,
            })),
            scrollTop: action.scrollTop,
            selectedValue: action.selectedValues ? action.selectedValues.join(",") : null,
          },
        },
      }

    case "CHANGE_VALUE_GENDERS":
      return {
        ...state,
        // activeField: null,
        fields: {
          ...state.fields,
          genders: {
            ...state.fields.genders,
            display: {
              text: action.displayText || null,
            },
            options: state.fields.genders.options.map(option => ({
              ...option,
              selected: action.selectedValues && action.selectedValues.indexOf(option.value) >= 0,
            })),
            scrollTop: action.scrollTop,
            selectedValue: action.selectedValues ? action.selectedValues.join(",") : null,
          },
        },
      }

    case "CHANGE_VALUE_JOB_TITLES":
      // Handle the job titles filter checkbox change event
      // Set `selected` to be whether or not each option's value is in the array of selected values,
      // do not close this active field, set the overall `selectedValue` for this field,
      // and set the display value
      return {
        ...state,
        // activeField: null,
        fields: {
          ...state.fields,
          jobTitles: {
            ...state.fields.jobTitles,
            display: {
              text: action.displayText || null,
            },
            options: state.fields.jobTitles.options.map(option => ({
              ...option,
              selected: action.selectedValues && action.selectedValues.indexOf(option.value) >= 0,
            })),
            scrollTop: action.scrollTop,
            selectedValue: action.selectedValues ? action.selectedValues.join(",") : null,
          },
        },
      }

    case "CHANGE_VALUE_LOCATIONS":
      // Handle the locations filter submit button event
      // Set `selected` to be whether or not each option's value is in the array of selected values,
      // do not close this active field, set the overall `selectedValue` for this field,
      // and set the display value
      return {
        ...state,
        // activeField: null,
        fields: {
          ...state.fields,
          locations: {
            ...state.fields.locations,
            display: {
              text: action.displayText || null,
            },
            selectedValue: action.selectedValues ? action.selectedValues.join(",") : null,
          },
        },
      }

    case "CHANGE_VALUE_REVIEW":
      // Handle the review filter radio change event
      // Set `selected` to be whether or not the selected value matches the option value,
      // close this active field by setting `activeField` to null, set the overall
      // `selectedValue` for this field, and set the display value to be the value
      // of the selected option
      return {
        ...state,
        activeField: null,
        fields: {
          ...state.fields,
          review: {
            ...state.fields.review,
            display: {
              text: state.fields.review.options.find(opt => opt.value === action.newValue).label,
            },
            options: state.fields.review.options.map(option => ({
              ...option,
              selected: option.value === action.newValue,
            })),
            selectedValue: action.newValue,
          },
        },
      }

    case "CHANGE_VALUE_START_DATE": {
      // Set the selected date value, and the display date.
      // Also sets the minimum value for the end date field
      // to avoid invalid date ranges being selected
      return {
        ...state,
        activeField: null,
        fields: {
          ...state.fields,
          startDate: {
            ...state.fields.startDate,
            display: {
              ...state.fields.startDate.display,
              text: action.newValue ? action.newValue.format(DATEFORMAT) : null,
            },
            selectedValue: action.newValue,
          },
          endDate: {
            ...state.fields.endDate,
            minValue: action.newValue ? action.newValue.startOf("day") : null,
          },
        },
      }
    }

    case "CHANGE_VALUE_VIEW_MODE":
      // Handle the view mode filter radio change event
      // Set `selected` to be whether or not the selected value matches the option value,
      // close this active field by setting `activeField` to null, set the overall
      // `selectedValue` for this field, and set the display value to be the short label
      // of the selected option
      return {
        ...state,
        activeField: null,
        fields: {
          ...state.fields,
          viewMode: {
            ...state.fields.viewMode,
            display: {
              i18nKey: state.fields.viewMode.options.find(opt => opt.value === action.newValue)
                .labelShort,
            },
            options: state.fields.viewMode.options.map(option => ({
              ...option,
              selected: option.value === action.newValue,
            })),
            selectedValue: action.newValue,
          },
        },
      }
    case "CHANGE_VALUE_MANAGER":
      // Handle the selection of a manager filter.
      // Set `selected` to be whether or not the selected value matches the option value,
      // The display value for the manager will be their name.
      return {
        ...state,
        activeField: null,
        fields: {
          ...state.fields,
          managers: {
            ...state.fields.managers,
            display: {
              text: action.newValue.name,
            },
            options: state.fields.managers.options.map(option => ({
              ...option,
              selected: option.id === action.newValue.id,
            })),
            selectedValue: action.newValue.employeeId,
          },
        },
      }
    case "CLEAR_VALUE_MANAGER":
      // Handle the clear manager filter event
      // Set `selected` to be false for all managers,
      // Set the selected value to be a dummy person.
      // Set text to none
      return {
        ...state,
        activeField: null,
        fields: {
          ...state.fields,
          managers: {
            ...state.fields.managers,
            display: {
              text: "None",
            },
            options: state.fields.managers.options.map(option => ({
              ...option,
              selected: false,
            })),
            selectedValue: -1,
          },
        },
      }

    case "CONFIGURE_VIEW_MODE_FOR_USER": {
      // If the user is an admin, they get to see the "Everyone" option,
      // but managers do not get this option
      const viewModeOptions = []

      if (action.user.isAdmin) {
        viewModeOptions.push({
          label: "Filters.Standard.ViewMode.Options.Everyone",
          labelShort: "Filters.Standard.ViewMode.Options.Everyone",
          value: "ALL",
          selected: false,
        })
      }

      viewModeOptions.push({
        label: "Filters.Standard.ViewMode.Options.Direct",
        labelShort: "Filters.Standard.ViewMode.Options.DirectShort",
        value: "DIRECT",
        selected: false,
      })

      if (action.user.hasSubEmployees) {
        viewModeOptions.push({
          label: "Filters.Standard.ViewMode.Options.Indirect",
          labelShort: "Filters.Standard.ViewMode.Options.IndirectShort",
          value: "INDIRECT",
          selected: false,
        })
      }

      // Set the selected value to be the first option
      viewModeOptions[0].selected = true

      return {
        ...state,
        fields: {
          ...state.fields,
          viewMode: {
            ...state.fields.viewMode,
            display: {
              i18nKey: viewModeOptions[0].labelShort,
            },
            isLoading: false,
            options: viewModeOptions,
            selectedValue: viewModeOptions[0].value,
          },
        },
      }
    }

    case "ERROR_LOADING_AVAILABLE_FILTER_LIST_OPTIONS":
      return {
        ...state,
        errorMessageKey: "Filters.Errors.LoadingOptions",
        fields: {
          ...state.fields,
          appraisalLevels: { ...state.fields.appraisalLevels, isLoading: false, options: [] },
          review: { ...state.fields.review, options: [] },
        },
      }

    case "RECEIVE_FILTER_LIST_OPTIONS":
      return {
        ...state,
        errorMessageKey: null,
        fields: {
          ...state.fields,

          appraisalLevels: {
            ...state.fields.appraisalLevels,
            isLoading: false,
            options: action.data.appraisalLevels,
          },

          generations: {
            ...state.fields.generations,
            isLoading: false,
            options: action.data.generations,
          },

          genders: {
            ...state.fields.gender,
            isLoading: false,
            options: action.data.gender,
          },

          jobTitles: {
            ...state.fields.jobTitles,
            isLoading: false,
            options: action.data.jobTitles,
          },

          locations: {
            ...state.fields.locations,
            isLoading: false,
            options: action.data.locations,
          },

          adminLocations: {
            ...state.fields.adminLocations,
            isLoading: false,
            options: action.data.adminLocations,
          },

          managers: {
            ...state.fields.managers,
            isLoading: false,
            options: action.data.managers,
          },

          review: { ...state.fields.review, isLoading: false, options: action.data.reviews },
        },
      }

    case "REQUEST_FILTER_LIST_OPTIONS":
      return {
        ...state,
        errorMessageKey: null,
        fields: {
          ...state.fields,
          appraisalLevels: {
            ...state.fields.appraisalLevels,
            isLoading: true,
            options: [],
          },
          review: { ...state.fields.review, isLoading: true, options: [] },
        },
      }

    case "RESET_ALL_FILTERS":
      // Reset - close all filters, remove selected values, reset scroll positions etc
      return {
        ...state,
        activeField: null,
        advancedFiltersOpen: false,
        errorMessageKey: null,
        fields: {
          ...state.fields,
          appraisalLevels: {
            ...state.fields.appraisalLevels,
            display: {
              text: null,
            },
            isLoading: false,
            options: state.fields.appraisalLevels.options.map((opt) => {
              return { ...opt, selected: false }
            }),
            scrollTop: 0,
            selectedValue: null,
          },
          dataMode: {
            ...state.fields.dataMode,
            display: {
              i18nKey: "Filters.Standard.DataMode.Options.Live",
            },
            options: state.fields.dataMode.options.map((opt, i) => {
              return { ...opt, selected: i === 0 }
            }),
            selectedValue: "LIVE",
          },
          endDate: {
            ...state.fields.endDate,
            display: {
              text: null,
            },
            selectedValue: null,
          },

          generations: {
            ...state.fields.generations,
            display: {
              text: null,
            },
            isLoading: false,
            options: state.fields.generations.options.map((opt) => {
              return { ...opt, selected: false }
            }),
            scrollTop: 0,
            selectedValue: null,
          },

          genders: {
            ...state.fields.genders,
            display: {
              text: null,
            },
            isLoading: false,
            options: state.fields.genders.options.map((opt) => {
              return { ...opt, selected: false }
            }),
            scrollTop: 0,
            selectedValue: null,
          },

          jobTitles: {
            ...state.fields.jobTitles,
            display: {
              text: null,
            },
            isLoading: false,
            options: state.fields.jobTitles.options.map((opt) => {
              return { ...opt, selected: false }
            }),
            scrollTop: 0,
            selectedValue: null,
          },

          locations: {
            ...state.fields.locations,
            display: {
              text: null,
            },
            selectedValue: null,
          },

          adminLocations: {
            ...state.fields.adminLocations,
            display: {
              text: null,
            },
            selectedValue: null,
          },

          managers: {
            ...state.fields.managers,
            display: {
              text: "None",
            },
            isLoading: false,
            options:
              state.fields.managers.options &&
              state.fields.managers.options.map((opt) => {
                return { ...opt, selected: false }
              }),
            selectedValue: -1,
          },

          review: {
            ...state.fields.review,
            display: {
              text: "None",
            },
            isLoading: false,
            options: state.fields.review.options.map((opt, i) => {
              return { ...opt, selected: i === 0 }
            }),
            selectedValue: "-1",
          },

          startDate: {
            ...state.fields.startDate,
            display: {
              text: null,
            },
            selectedValue: null,
          },

          // viewMode is handled by dispatching the CONFIGURE_VIEW_MODE_FOR_USER action
        },
      }

    case "SET_ACTIVE_FIELD":
      return {
        ...state,
        activeField: action.fieldKey,
        // Reset any half way down scroll positions on checkbox lists
        fields: {
          ...state.fields,
          appraisalLevels: {
            ...state.fields.appraisalLevels,
            scrollTop: 0,
          },
          generations: {
            ...state.fields.generations,
            scrollTop: 0,
          },
          genders: {
            ...state.fields.genders,
            scrollTop: 0,
          },
          jobTitles: {
            ...state.fields.jobTitles,
            scrollTop: 0,
          },
        },
      }

    case "SET_FULLSCREEN_FILTERS_VISIBLE":
      return { ...state, showFiltersFullScreen: action.isVisible }

    case "SET_FILTERS_PINNED":
      return { ...state, filtersPinned: action.isPinned }

    case "SET_ADVANCED_FILTERS_OPEN":
      return {
        ...state,
        activeField: null,
        advancedFiltersOpen: action.isOpen,
        // Reset any half way down scroll positions on checkbox lists
        fields: {
          ...state.fields,
          appraisalLevels: {
            ...state.fields.appraisalLevels,
            scrollTop: 0,
          },
          generations: {
            ...state.fields.generations,
            scrollTop: 0,
          },
          genders: {
            ...state.fields.genders,
            scrollTop: 0,
          },
          jobTitles: {
            ...state.fields.jobTitles,
            scrollTop: 0,
          },
          managers: {
            ...state.fields.managers,
            scrollTop: 0,
          },
        },
      }

    default:
      return state
  }
}



// WEBPACK FOOTER //
// ./src/state/reducers/filterReducer.js